// canvas/layout.js: table layout calculator (plan-m2.md v2 M2.c-1).
// Synthesis §4.7 baseline: N/S/E/W compass seating (locked decision #6
// in §7 of the synthesis). Player always at S (bottom), partner at N
// (top), opponents at W/E.
//
// All seat positions, the trick area, the scoreboard, and the bidding
// zone are derived from this single layout object. No hard-coded
// positions live in renderers, that's the rule that keeps the four
// rendering modules (table, hand, trick area, bidding zone) in step.
//
// M2.c-2 + M2.c-3 + M2.c-5 build on this; M2.c-1 only uses {table,
// seats, trickArea}.

import { computeBidGrid, bidLabelWidth, gridHeight } from './bid-grid.js';

export { computeCardLayout } from '../layout.js';

const SEAT_IDS = /** @type {const} */ ([1, 2, 3, 4]);

// B1-4: turn-timer ring outer box (px). TurnTimerRing.jsx centres its
// SVG on this box; layout.timerRing pins the You-plate instance.
const TIMER_RING_SIZE = 36;
const SEAT_ALIGN = /** @type {const} */ ({
  1: 'bottom',
  2: 'left',
  3: 'top',
  4: 'right',
});

/**
 * @typedef {{
 *   x: number, y: number, w: number, h: number,
 *   orientation: 'horizontal-large'|'horizontal-small'|'stack',
 *   cardW: number, cardH: number,
 *   overlap: number,
 *   anchorX: number, anchorY: number,
 * }} HandArea
 */

/**
 * @typedef {{
 *   id: 1|2|3|4,
 *   align: 'bottom'|'left'|'top'|'right',
 *   x: number, y: number, w: number, h: number,
 *   angle: 0,
 *   centerX: number, centerY: number,
 *   handArea: HandArea,
 *   pillSide?: 'top'|'bottom'|'left'|'right',
 * }} SeatBox
 */

/**
 * @typedef {{
 *   canvasW: number, canvasH: number,
 *   isMobile: boolean,
 *   table:       {x:number,y:number,w:number,h:number},
 *   seats:       SeatBox[],
 *   trickArea:   {x:number,y:number,w:number,h:number},
 *   scoreboard:  {x:number,y:number,w:number,h:number},
 *   biddingZone: {x:number,y:number,w:number,h:number},
 *   upCard:      {x:number,y:number,w:number,h:number},
 *   plateW: number, plateH: number,
 *   badgeW: number, badgeH: number,
 *   portrait?: boolean,
 *   compact?: boolean,
 *   tapToRaise?: boolean,
 *   timerRing?:  {x:number,y:number,w:number,h:number},
 *   trumpBadge?: {x:number,y:number,w:number,h:number},
 *   bidBands?: Array<import('./bid-grid.js').BidBand>,
 * }} TableLayout
 */

export function timerRingRectForPlate(plate) {
  return {
    x: plate.x + plate.w - Math.round(TIMER_RING_SIZE / 2),
    y: plate.y - Math.round(TIMER_RING_SIZE / 2),
    w: TIMER_RING_SIZE,
    h: TIMER_RING_SIZE,
  };
}

// V3: desktop ring placement. The corner box above shares its x (the
// plate's right edge) but sits 18px above the plate, which on desktop is
// the bottom row of the hand fan, so it covered the right-hand card's
// lower corner. Centred on the plate the ring is below the hand fan
// (the fan ends 8px above the plate) and covers no card. The clamp keeps
// it inside the canvas when the plate sits at the canvas bottom.
export function desktopTimerRingRectForPlate(plate, canvasH) {
  let y = plate.y + Math.round((plate.h - TIMER_RING_SIZE) / 2);
  if (y + TIMER_RING_SIZE > canvasH) y = canvasH - TIMER_RING_SIZE;
  if (y < 0) y = 0;
  return {
    x: plate.x + plate.w - Math.round(TIMER_RING_SIZE / 2),
    y,
    w: TIMER_RING_SIZE,
    h: TIMER_RING_SIZE,
  };
}

const LOG_BESIDE_MIN_W = 1000;
const LOG_BESIDE_MAX_W = 1240;
const LOG_COLUMN_W = 220;

/**
 * B3b U9: the networked table's layout. From 1000px to 1239px wide the
 * game log sits beside the table, so the table gives up 220px (the
 * 200px log column and its 16px gap, plus slack). Narrower screens
 * show the log below the table or in a drawer, so they keep the full
 * width; 1240px and up has room for both.
 *
 * @param {number} viewportW
 * @param {number} viewportH
 * @returns {TableLayout}
 */
export function networkedTableLayout(viewportW, viewportH) {
  const logBeside = viewportW >= LOG_BESIDE_MIN_W && viewportW < LOG_BESIDE_MAX_W;
  return computeTableLayout(
    logBeside ? viewportW - LOG_COLUMN_W : viewportW,
    viewportH,
    { reserveLogColumn: logBeside },
  );
}

/**
 * TableLayout additions in B1: upCard (both compositions, the box
 * upcard.js draws into, kept clear by DOM overlays), timerRing +
 * trumpBadge (portrait only: the 36px turn-timer box on the You
 * plate's top-right corner, and the parked trump-badge box left of
 * the scoreboard band).
 *
 * Compute the table layout for a given viewport.
 *
 * Mobile (W < 600): canvas fills viewport minus thin margins; plates 90×44
 * (matches the 44px touch-target minimum exactly).
 * Desktop (W >= 600): canvas capped at 1024×640 (16:10), plates 140×56.
 *
 * Seats are returned 1..4 indexed (array offset = seat_id - 1). Each seat's
 * (x,y) is the top-left of its nameplate rectangle.
 *
 * @param {number} viewportW
 * @param {number} viewportH
 * @param {{ reserveLogColumn?: boolean }} [options]
 * @returns {TableLayout}
 */
export function computeTableLayout(viewportW, viewportH, options = {}) {
  // T14: phones held upright get a purpose-built vertical composition,
  // opponent strip on top, trick center, hand docked at the bottom.
  // ADDITIVE branch: desktop + landscape output is frozen by
  // layout-portrait.test.js against a pre-T14 snapshot.
  if (viewportH > viewportW && viewportW < 700) {
    return computePortraitLayout(viewportW, viewportH);
  }
  // Z3: every landscape viewport under 400px tall, whatever its width,
  // gets the short-landscape composition. 568x320 used to fall into the
  // mobile branch below, whose 520px canvas floor put the hand at y 458.
  // The header and its gap take 80px; wider than 760px the event log
  // keeps its 200px column beside the table (16px gap).
  if (viewportW > viewportH && viewportH < 400) {
    const canvasW = viewportW > 760 ? Math.min(1024, viewportW - 232) : viewportW - 16;
    return computeCompactLayout(canvasW, viewportH - 80);
  }
  const isMobile = viewportW < 600;

  // Canvas sizing. Desktop uses a 16:10 box up to 1024×640. Mobile fills
  // the viewport minus thin margins.
  let canvasW;
  let canvasH;
  if (isMobile) {
    canvasW = Math.max(320, viewportW - 16);
    canvasH = Math.max(520, viewportH - 48);
  } else {
    canvasW = Math.min(1024, viewportW - 64);
    canvasH = Math.min(640, viewportH - 80);
    if (canvasW / canvasH > 16 / 10) {
      canvasW = Math.round((canvasH * 16) / 10);
    } else if (!options.reserveLogColumn) {
      canvasH = Math.round((canvasW * 10) / 16);
    }
    // Y4: a landscape phone (667x375 → 472x295) can't hold the desktop
    // composition: the 101px hand covered the side plates and the
    // up-card. Short canvases get their own composition.
    if (canvasH < 400) return computeCompactLayout(canvasW, canvasH);
  }

  // Nameplate dimensions. The 44×44 minimum is iOS/Material's touch target;
  // we exceed it on both axes on desktop and meet it on width on mobile.
  const plateW = isMobile ? 100 : 140;
  const plateH = isMobile ? 48 : 56;

  // Trump badge, synthesis §4.7 mandates ≥48px and "never occluded".
  const badgeW = 48;
  const badgeH = 48;

  const margin = isMobile ? 8 : 24;
  const cx = Math.round(canvasW / 2);
  const cy = Math.round(canvasH / 2);

  // Card sizes, player's hand cards are larger (fan, hover-lift), other
  // seats' cards are smaller face-down sprites. 3.5:2.5 aspect (M1 card).
  const cardW_main  = isMobile ? 56 : 72;
  const cardH_main  = Math.round(cardW_main  * 3.5 / 2.5);  // 78  / 101
  const cardW_small = isMobile ? 34 : 44;
  const cardH_small = Math.round(cardW_small * 3.5 / 2.5);  // 48  / 62
  const handFanGap = 8;
  const fanOverlap_main  = isMobile ? 18 : 24;
  const fanOverlap_small = isMobile ? 10 : 14;

  // Trick area, center of canvas, sized to leave clearance around all
  // four seats AND not collide with the scoreboard / bidding zone.
  const trickClearanceX = plateW + margin + 16;
  const trickClearanceY = plateH + margin + 16;
  const trickW = Math.max(180, canvasW - 2 * trickClearanceX);
  const trickH = Math.max(120, canvasH - 2 * trickClearanceY);
  const trickArea = {
    x: cx - Math.round(trickW / 2),
    y: cy - Math.round(trickH / 2),
    w: trickW,
    h: trickH,
  };

  // B1-1: the up-card's canvas rect, centred on the trick area with the
  // exact box upcard.js draws into (110px desktop / 90px mobile wide,
  // 3.5:2.5 aspect). DOM overlays (bidding zone, trump splash) keep
  // clear of this rect so the card stays readable during bidding.
  const upCardW = isMobile ? 90 : 110;
  const upCardH = Math.round(upCardW * 3.5 / 2.5);
  const upCard = {
    x: trickArea.x + Math.round(trickArea.w / 2) - Math.round(upCardW / 2),
    y: trickArea.y + Math.round(trickArea.h / 2) - Math.round(upCardH / 2),
    w: upCardW,
    h: upCardH,
  };

  // Z6: on short canvases (800x600 gives 736x460) the hand fan above
  // the You plate covered the bottom of the up-card. There the hand
  // drops to the bottom edge and the You plate moves to its right, so
  // the card keeps its full size and 8px of clear space.
  const fanW_main = cardW_main + (5 - 1) * (cardW_main - fanOverlap_main);
  // B3c: the stacked hand sits 6px higher than the fan gap needs, so the
  // You pill above the plate keeps 4px from it. B3c r3: the beside-plate
  // choice uses that same spot (with the old one, 1024x628 left the
  // up-card 2px above the hand).
  const handLift = 6;
  const stackedHandY = canvasH - plateH - margin - handFanGap - handLift - cardH_main;
  const handBesidePlate = upCard.y + upCard.h + 8 > stackedHandY;
  // B3c r3: on the shortest desktop canvases (400-419px tall) the hand on
  // the bottom edge still ended 2px above the up-card's bottom. It drops
  // into the bottom margin until it is 8px clear, but keeps 14px to the
  // canvas edge (the felt starts 10px in).
  const besideHandY = Math.max(canvasH - margin - cardH_main,
    Math.min(canvasH - 14 - cardH_main, upCard.y + upCard.h + 8));

  /** @type {SeatBox[]} */
  const seats = SEAT_IDS.map((id) => {
    let x;
    let y;
    if (id === 1 && handBesidePlate) {
      x = cx - Math.round(fanW_main / 2) + fanW_main + handFanGap;
      y = canvasH - plateH - margin;
    } else if (id === 1) { // S, bottom (you)
      x = cx - Math.round(plateW / 2);
      y = canvasH - plateH - margin;
    } else if (id === 2) { // W, left
      x = margin;
      y = cy - Math.round(plateH / 2);
    } else if (id === 3) { // N, top (partner)
      x = cx - Math.round(plateW / 2);
      y = margin;
    } else {               // E, right
      x = canvasW - plateW - margin;
      y = cy - Math.round(plateH / 2);
    }

    // Per-seat hand area. Renderers use these as the anchor + dims for
    // the fan/stack of cards. Cards may extend SLIGHTLY past the area's
    // declared rect on hover lift (handled by the hand renderer).
    /** @type {HandArea} */
    let handArea;
    if (id === 1) {
      // Bottom: large horizontal fan ABOVE the nameplate (Z6: beside it,
      // on the bottom edge, when the canvas is short).
      const handY = handBesidePlate ? besideHandY : stackedHandY;
      handArea = {
        x: cx - Math.round(fanW_main / 2),
        y: handY,
        w: fanW_main,
        h: cardH_main,
        orientation: 'horizontal-large',
        cardW: cardW_main, cardH: cardH_main,
        overlap: fanOverlap_main,
        anchorX: cx,
        anchorY: handY + cardH_main,
      };
    } else if (id === 3) {
      // Top (partner): small horizontal fan BELOW the nameplate.
      const fanW = cardW_small + (5 - 1) * (cardW_small - fanOverlap_small);
      handArea = {
        x: cx - Math.round(fanW / 2),
        y: y + plateH + handFanGap,
        w: fanW,
        h: cardH_small,
        orientation: 'horizontal-small',
        cardW: cardW_small, cardH: cardH_small,
        overlap: fanOverlap_small,
        anchorX: cx,
        anchorY: y + plateH + handFanGap + cardH_small,
      };
    } else if (id === 2) {
      // Left: vertical stack to the RIGHT of the nameplate (inner side).
      const stackH = cardH_small + (5 - 1) * 6;  // 6px stagger
      handArea = {
        x: x + plateW + handFanGap,
        y: cy - Math.round(stackH / 2),
        w: cardW_small,
        h: stackH,
        orientation: 'stack',
        cardW: cardW_small, cardH: cardH_small,
        overlap: 6,
        anchorX: x + plateW + handFanGap,
        anchorY: cy,
      };
    } else {
      // Right: vertical stack to the LEFT of the nameplate (inner side).
      const stackH = cardH_small + (5 - 1) * 6;
      handArea = {
        x: x - handFanGap - cardW_small,
        y: cy - Math.round(stackH / 2),
        w: cardW_small,
        h: stackH,
        orientation: 'stack',
        cardW: cardW_small, cardH: cardH_small,
        overlap: 6,
        anchorX: x - handFanGap - cardW_small,
        anchorY: cy,
      };
    }

    return {
      id,
      align: SEAT_ALIGN[id],
      x, y, w: plateW, h: plateH,
      angle: 0,
      centerX: x + Math.round(plateW / 2),
      centerY: y + Math.round(plateH / 2),
      handArea,
    };
  });

  // Scoreboard, top-right corner, just outside the partner's nameplate.
  // (M2.c-5 added mini trump pip + US/THEM scores; M2.c-6 adds the
  // hand-index + game-target line below.) Two-row panel: row 1 score,
  // row 2 hand progress. Mobile panel width sized so a 2-digit score
  // ("US 10 | THEM 10") doesn't clip at the 11px font scale.
  const scoreW = isMobile ? 116 : 140;
  const scoreH = isMobile ? 48 : 60;
  const scoreboard = {
    x: canvasW - scoreW - margin,
    y: margin,
    w: scoreW,
    h: scoreH,
  };

  // Y2: the bid buttons sit in the band between the partner's fan and
  // the up-card, centred, close to the card being bid on. When that band
  // is too thin for the rows (800x600: 3px) they fall back to the upper-
  // left corner, which is clear of the partner fan, scoreboard and
  // up-card. bid-grid.js picks the first band that holds the round.
  const partnerFan = seats[2].handArea;
  const centreTop = partnerFan.y + partnerFan.h + 3;
  const centreBand = {
    x: 8, y: centreTop, w: canvasW - 16, h: upCard.y - 3 - centreTop,
    anchor: /** @type {const} */ ('center'), fill: false, prefW: 520,
  };
  const cornerBand = {
    x: 8, y: 8, w: partnerFan.x - 16, h: seats[1].handArea.y - 16,
    anchor: /** @type {const} */ ('top'), fill: true,
  };
  const bidBands = centreBand.h > 0 ? [centreBand, cornerBand] : [cornerBand];
  // The reserved zone is the band that holds the largest (seven-button)
  // round, cut to that grid's width; round one sits inside it.
  const grid7 = computeBidGrid({ isMobile, bidBands }, 7);
  const usedBand = bidBands[grid7.bandIndex];
  const biddingZone = usedBand === centreBand
    ? { x: grid7.zone.x, y: centreBand.y, w: grid7.zone.w, h: centreBand.h }
    : { x: cornerBand.x, y: cornerBand.y, w: cornerBand.w, h: cornerBand.h };

  return {
    canvasW,
    canvasH,
    isMobile,
    table: { x: 0, y: 0, w: canvasW, h: canvasH },
    seats,
    trickArea,
    scoreboard,
    biddingZone,
    upCard,
    bidBands,
    // V3: the You-plate ring rides the plate's right edge, vertically
    // centred (TurnTimerRing.jsx draws layout.timerRing there), clear of
    // the hand fan above the plate.
    timerRing: desktopTimerRingRectForPlate(seats[0], canvasH),
    plateW,
    plateH,
    badgeW,
    badgeH,
  };
}

/**
 * Y4, short landscape composition: every landscape viewport under 400px
 * tall (Z3), such as 667x375, 844x390, 640x320 and 568x320, plus small
 * windows whose 16:10 canvas is under 400px tall.
 *
 *   ┌──────────────────────────────────────────┐
 *   │ [W plate] [badge] [N plate]     [E plate]│
 *   │  W fan            N fan          E fan   │
 *   │      [up-card] [   bid buttons    ]      │  trick area
 *   │ [You plate] ████ hand ████ [scoreboard]  │
 *   └──────────────────────────────────────────┘
 *
 * All three opponents share the top row, so the hand, the up-card, the
 * plates, the fans and the Pass pills never cover each other. Each seat
 * names its pill edge (pillSide). The top-row pills sit just below the
 * plate with the fans below them, the You pill just above its plate.
 *
 * Z2: when that stack leaves the trick band too short for two rows of
 * bid buttons (canvases under ~294px tall, such as 640x320 and
 * 568x320), each opponent's fan and pill move into a column beside its
 * plate (side fans), so the top row is one plate tall.
 *
 * @param {number} canvasW
 * @param {number} canvasH
 * @returns {TableLayout & {compact: true, tapToRaise: boolean}}
 */
function computeCompactLayout(canvasW, canvasH) {
  const margin = 6;
  const cx = Math.round(canvasW / 2);
  const plateW = 96;
  const plateH = 44;
  const badgeW = 48;
  const badgeH = 48;
  const pillW = 52;            // bid-bubbles BUBBLE_W
  const pillH = 20;            // bid-bubbles BUBBLE_H
  const minHandH = Math.round(44 * 3.5 / 2.5);
  // Two rows of 40px bid buttons with the dealer line.
  const twoRowsH = gridHeight(2, 40);

  const scoreW = 116;
  const scoreH = 44;
  const scoreboard = {
    x: canvasW - scoreW - margin,
    y: canvasH - scoreH - margin,
    w: scoreW,
    h: scoreH,
  };

  // The bottom row (You plate, its pill and timer ring, the hand) ends
  // the trick band here, whatever the hand's size.
  const youPlateY = canvasH - plateH - margin;
  const youPillTop = youPlateY - pillH;
  const timerRing = timerRingRectForPlate({ x: margin, y: youPlateY, w: plateW, h: plateH });
  const bottomRowTop = Math.min(canvasH - margin - minHandH, timerRing.y, youPillTop, scoreboard.y);

  // Stacked: pill under the plate, fan under the pill.
  const stackedFanY = margin + plateH + pillH + 2;
  const stackedBandTop = stackedFanY + Math.round(24 * 3.5 / 2.5) + 4;
  // B3c r2: side fans need three plate + pill columns with 4px between
  // them. Below 464px the opponent plates give up a few pixels (89px at
  // 444) so the fans still move out of the trick band; stacked fans
  // there left the band too short for the round-two grid, which then
  // covered the top fans.
  const oppPlateW = Math.min(plateW, Math.floor((canvasW - 2 * margin - 8) / 3) - pillW);
  const sideFits = oppPlateW >= 84;
  const sideFans = sideFits && bottomRowTop - 4 - stackedBandTop < twoRowsH;
  const topPlateW = sideFans ? oppPlateW : plateW;

  const cardW_small = sideFans ? 17 : 24;
  const cardH_small = Math.round(cardW_small * 3.5 / 2.5); // 24 / 34
  const miniFanOverlap = sideFans ? 9 : 12;
  const miniFanW = cardW_small + 4 * (cardW_small - miniFanOverlap); // 49 / 72

  /** @type {Record<number, {x:number, y:number}>} */
  const plates = sideFans
    ? {
      1: { x: margin, y: youPlateY },
      2: { x: margin, y: margin },
      3: { x: cx - Math.round((topPlateW + pillW) / 2), y: margin },
      4: { x: canvasW - topPlateW - margin, y: margin },
    }
    : {
      1: { x: margin, y: youPlateY },
      2: { x: margin, y: margin },
      3: { x: cx - Math.round(plateW / 2), y: margin },
      4: { x: canvasW - plateW - margin, y: margin },
    };

  /** @type {Record<number, 'top'|'bottom'|'left'|'right'>} */
  const pillSides = sideFans
    ? { 1: 'top', 2: 'right', 3: 'right', 4: 'left' }
    : { 1: 'top', 2: 'bottom', 3: 'bottom', 4: 'bottom' };

  // Top-row bottom edge: the fans (stacked) or the plates (side fans).
  const topRowBottom = sideFans ? margin + plateH : stackedFanY + cardH_small;

  // Trump badge: parked in the top row between W's and N's plates when
  // it fits with its label (badgeW + 28 wide) and the fans are stacked
  // (beside side fans its label would cut into the trick band);
  // otherwise at the left end of the trick band.
  const westPlateRight = plates[2].x + plateW;
  const topGap = plates[3].x - westPlateRight;
  const badgeInTopRow = !sideFans && topGap >= badgeW + 28 + 8;
  const trumpBadge = badgeInTopRow
    ? { x: westPlateRight + Math.floor((topGap - badgeW) / 2), y: margin, w: badgeW, h: badgeH }
    : { x: margin, y: topRowBottom + 4, w: badgeW, h: badgeH };
  const bandTop = Math.max(topRowBottom,
    badgeInTopRow ? trumpBadge.y + badgeH + 18 : 0) + 4;

  // Hand: between the timer ring on the You plate and the scoreboard.
  // 44px effective hits while they fit; tap-to-raise below that. The
  // cards grow past 44px wide only while the band above keeps room for
  // two rows of bid buttons.
  const handX0 = timerRing.x + timerRing.w + 4;
  const handAvail = scoreboard.x - 4 - handX0;
  const maxCardH = canvasH - margin - (bandTop + twoRowsH + 4);
  const cardW_main = Math.max(44, Math.min(56, handAvail - 4 * 44, Math.floor(maxCardH / 1.4)));
  const step = Math.min(44, Math.floor((handAvail - cardW_main) / 4));
  const fanOverlap_main = cardW_main - step;
  const fanW = cardW_main + 4 * step;
  const cardH_main = Math.round(cardW_main * 3.5 / 2.5);
  const handY = canvasH - cardH_main - margin;
  const handX = handX0 + Math.max(0, Math.floor((handAvail - fanW) / 2));
  const tapToRaise = step < 44;

  /** @type {SeatBox[]} */
  const seats = SEAT_IDS.map((id) => {
    const { x, y } = plates[id];
    const seatW = id === 1 ? plateW : topPlateW;
    /** @type {HandArea} */
    let handArea;
    if (id === 1) {
      handArea = {
        x: handX, y: handY, w: fanW, h: cardH_main,
        orientation: 'horizontal-large',
        cardW: cardW_main, cardH: cardH_main,
        overlap: fanOverlap_main,
        anchorX: handX + Math.round(fanW / 2),
        anchorY: handY,
      };
    } else {
      // Side fans: the fan tops the pill's column beside the plate.
      const columnX = pillSides[id] === 'left' ? x - pillW : x + seatW;
      const fanX = sideFans
        ? columnX + Math.round((pillW - miniFanW) / 2)
        : x + Math.round((seatW - miniFanW) / 2);
      const fanY = sideFans ? y : stackedFanY;
      handArea = {
        x: fanX,
        y: fanY,
        w: miniFanW,
        h: cardH_small,
        orientation: 'horizontal-small',
        cardW: cardW_small, cardH: cardH_small,
        overlap: miniFanOverlap,
        anchorX: fanX + Math.round(miniFanW / 2),
        anchorY: fanY + cardH_small,
      };
    }
    return {
      id,
      align: SEAT_ALIGN[id],
      x, y, w: seatW, h: plateH,
      angle: 0,
      centerX: x + Math.round(seatW / 2),
      centerY: y + Math.round(plateH / 2),
      handArea,
      pillSide: pillSides[id],
    };
  });

  // Trick band: everything between the top row and the bottom row.
  const bandBottom = Math.min(handY, timerRing.y, youPillTop, scoreboard.y) - 4;
  const trickArea = {
    x: margin,
    y: bandTop,
    w: canvasW - 2 * margin,
    h: Math.max(40, bandBottom - bandTop),
  };

  // Up-card and bid buttons share the trick band side by side (the
  // band is too short to stack them). A parked badge takes the left end.
  const pairX0 = badgeInTopRow ? trickArea.x : trumpBadge.x + badgeW + 8;
  const pairW = trickArea.x + trickArea.w - pairX0;
  const upCardW = Math.max(30, Math.min(64, Math.floor((trickArea.h - 8) / 1.4)));
  const upCardH = Math.round(upCardW * 3.5 / 2.5);
  // Wide enough for four columns of round-two buttons at the 12px font.
  const bidBandW = Math.min(pairW - upCardW - 12, 4 * bidLabelWidth(12) + 3 * 4 + 4 + 24);
  const pairStart = pairX0 + Math.max(0, Math.floor((pairW - (upCardW + 12 + bidBandW)) / 2));
  const upCard = {
    x: pairStart,
    y: trickArea.y + Math.round((trickArea.h - upCardH) / 2),
    w: upCardW,
    h: upCardH,
  };
  const biddingZone = {
    x: upCard.x + upCardW + 12,
    y: trickArea.y,
    w: bidBandW,
    h: trickArea.h,
  };
  const bidBands = [{ ...biddingZone, anchor: /** @type {const} */ ('center'), fill: true }];

  return {
    canvasW,
    canvasH,
    isMobile: true,
    compact: true,
    tapToRaise,
    table: { x: 0, y: 0, w: canvasW, h: canvasH },
    seats,
    trickArea,
    scoreboard,
    biddingZone,
    upCard,
    timerRing,
    trumpBadge,
    bidBands,
    plateW,
    plateH,
    badgeW,
    badgeH,
  };
}

/**
 * T14, portrait phone composition.
 *
 *   ┌─────────────────────────────┐
 *   │ [W plate] [N plate] [E plate]│  opponent strip + mini fans below
 *   │            [scoreboard]      │
 *   │                             │
 *   │         trick area          │
 *   │                             │
 *   │        bidding zone         │
 *   │         [You plate]         │
 *   │     █████ hand  dock ████   │  ≥44px effective hit zones
 *   └─────────────────────────────┘
 *
 * The canvas reserves vertical room for the T15 DOM header above it
 * (the view owns that); everything here is canvas-internal geometry.
 *
 * @param {number} viewportW
 * @param {number} viewportH
 * @returns {TableLayout & {portrait: true, tapToRaise: boolean}}
 */
function computePortraitLayout(viewportW, viewportH) {
  const canvasW = Math.max(300, viewportW - 8);
  // ~96px stays with the DOM: T15's fixed header row + safe-area dock.
  const canvasH = Math.max(480, viewportH - 96);

  const margin = 6;
  const cx = Math.round(canvasW / 2);

  // ── opponent strip: three compact plates sharing the top band ──
  const stripGap = 6;
  const plateW = Math.min(118, Math.floor((canvasW - 2 * margin - 2 * stripGap) / 3));
  const plateH = 44;
  const badgeW = 48;
  const badgeH = 48;

  const cardW_small = 22;
  const cardH_small = Math.round(cardW_small * 3.5 / 2.5); // 31
  const miniFanOverlap = 14;
  const miniFanW = cardW_small + 4 * (cardW_small - miniFanOverlap);

  // ── your hand dock: bottom, large cards, ≥44px effective hits ──
  // Effective hit width = cardW - overlap for every card but the last.
  // Pick the largest card that fits 5-up with 44px hits; below 320px
  // canvas width that's impossible, flag tapToRaise instead.
  let cardW_main = 84;
  let fanOverlap_main = cardW_main - 44;
  let fanW = cardW_main + 4 * (cardW_main - fanOverlap_main);
  let tapToRaise = false;
  if (fanW > canvasW - 2 * margin) {
    // Shrink the card, keep 44px hits as long as the fan fits. Cap at
    // 84, the equation can EXPAND the card on narrow canvases (fewer
    // pixels per hit means more pixels for the last card).
    cardW_main = Math.min(84, Math.floor(canvasW - 2 * margin - 4 * 44));
    if (cardW_main < 48) {
      // Very narrow: keep a readable 56px card, tighten the hits, and
      // let the view arm tap-to-raise (first tap lifts, second plays).
      cardW_main = 56;
      fanOverlap_main = Math.ceil((5 * cardW_main - (canvasW - 2 * margin)) / 4);
      tapToRaise = true;
    } else {
      fanOverlap_main = cardW_main - 44;
    }
    fanW = cardW_main + 4 * (cardW_main - fanOverlap_main);
  }
  const cardH_main = Math.round(cardW_main * 3.5 / 2.5);
  const handFanGap = 6;

  const handY = canvasH - cardH_main - margin;
  const youPlateY = handY - plateH - handFanGap - 8;

  /** @type {SeatBox[]} */
  const seats = SEAT_IDS.map((id) => {
    let x;
    let y;
    if (id === 1) {
      x = cx - Math.round(plateW / 2);
      y = youPlateY;
    } else if (id === 2) {
      x = margin;
      y = margin;
    } else if (id === 3) {
      x = cx - Math.round(plateW / 2);
      y = margin;
    } else {
      x = canvasW - plateW - margin;
      y = margin;
    }

    /** @type {HandArea} */
    let handArea;
    if (id === 1) {
      handArea = {
        x: cx - Math.round(fanW / 2),
        y: handY,
        w: fanW,
        h: cardH_main,
        orientation: 'horizontal-large',
        cardW: cardW_main, cardH: cardH_main,
        overlap: fanOverlap_main,
        anchorX: cx,
        anchorY: handY,
      };
    } else {
      // Opponents: tiny horizontal fans tucked under their plates.
      handArea = {
        x: x + Math.round((plateW - miniFanW) / 2),
        y: y + plateH + 4,
        w: miniFanW,
        h: cardH_small,
        orientation: 'horizontal-small',
        cardW: cardW_small, cardH: cardH_small,
        overlap: miniFanOverlap,
        anchorX: x + Math.round(plateW / 2),
        anchorY: y + plateH + 4 + cardH_small,
      };
    }

    return {
      id,
      align: SEAT_ALIGN[id],
      x, y, w: plateW, h: plateH,
      angle: 0,
      centerX: x + Math.round(plateW / 2),
      centerY: y + Math.round(plateH / 2),
      handArea,
    };
  });

  // ── scoreboard: right-aligned, directly under the strip's fans ──
  const stripBottom = margin + plateH + 4 + cardH_small;
  const scoreW = 116;
  const scoreH = 44;
  const scoreboard = {
    x: canvasW - scoreW - margin,
    y: stripBottom + 4,
    w: scoreW,
    h: scoreH,
  };

  // ── timer ring (B1-4): 36px box on the You plate's top-right
  // corner, the same box TurnTimerRing.jsx draws into. The bidding
  // zone below is placed against this box (not the plate) so the
  // ring never covers the bid buttons.
  const timerRing = timerRingRectForPlate(seats[0]);

  // ── bidding zone: full-width strip above the You plate AND the
  // timer ring (B1-4). Bottom edge clears the ring by 6px.
  const bidH = canvasH >= 700 ? 120 : canvasH >= 600 ? 88 : 74;
  const biddingZone = {
    x: margin,
    y: Math.max(stripBottom + 4, timerRing.y - 6 - bidH),
    w: canvasW - 2 * margin,
    h: bidH,
  };

  // ── trick area: everything between the scoreboard band and bidding ──
  const trickTop = scoreboard.y + Math.max(scoreH, badgeH + 18) + 4;
  const trickBottom = biddingZone.y - 4;
  // No rigid height floor: on a 568px-tall phone the band between
  // scoreboard and bidding is what it is, trick-area.js shrinks its
  // cards to fit (24px legibility floor lives there).
  const trickArea = {
    x: margin,
    y: trickTop,
    w: canvasW - 2 * margin,
    h: Math.max(40, trickBottom - trickTop),
  };

  // ── trump badge (B1-5): parked left of the scoreboard band, under
  // the opponent strip, instead of beside a seat plate where it
  // landed on top of the right-hand plate / mini fan. Disjoint from
  // every plate, every mini fan, the scoreboard, and the trick area
  // (edge-touch at most).
  const trumpBadge = {
    x: margin,
    y: stripBottom + 4,
    w: badgeW,
    h: badgeH,
  };

  // ── bid buttons (Y1): a round-two grid that does not fit the
  // reserved zone at 40px rows grows upward, up to the scoreboard and
  // badge band, instead of shrinking its buttons.
  const growTop = Math.max(scoreboard.y + scoreH, trumpBadge.y + badgeH + 18) + 4;
  const bidBottom = biddingZone.y + biddingZone.h;
  const bidBands = [{
    x: margin, y: growTop, w: canvasW - 2 * margin, h: bidBottom - growTop,
    anchor: /** @type {const} */ ('bottom'), fill: true,
  }];
  const grid7 = computeBidGrid({ portrait: true, bidBands }, 7);

  // ── up-card rect (B1-1/B1-2): centred on the trick area, the box
  // upcard.js draws into. The splash helper keeps clear of it. Y1: it
  // moves up, beside the scoreboard, to stay above the largest bid
  // grid; it shrinks only when even that space is too short.
  let portraitUpCardW = canvasH <= 480 ? 42 : canvasH <= 544 ? 70 : 90;
  let portraitUpCardH = Math.round(portraitUpCardW * 3.5 / 2.5);
  const upCardX = (w) => trickArea.x + Math.round(trickArea.w / 2) - Math.round(w / 2);
  const clearOfBand = (x, w) => x + w <= scoreboard.x - 4 && x >= trumpBadge.x + badgeW + 14 + 4;
  const upCardTopLimit = clearOfBand(upCardX(portraitUpCardW), portraitUpCardW)
    ? stripBottom + 4 : growTop;
  const upCardBottom = Math.min(biddingZone.y, grid7.zone.y) - 6;
  if (upCardBottom - upCardTopLimit < portraitUpCardH) {
    portraitUpCardW = Math.max(30, Math.floor((upCardBottom - upCardTopLimit) / 1.4));
    portraitUpCardH = Math.round(portraitUpCardW * 3.5 / 2.5);
  }
  const upCard = {
    x: upCardX(portraitUpCardW),
    y: Math.max(upCardTopLimit, Math.min(
      trickArea.y + Math.round(trickArea.h / 2) - Math.round(portraitUpCardH / 2),
      upCardBottom - portraitUpCardH)),
    w: portraitUpCardW,
    h: portraitUpCardH,
  };

  return {
    canvasW,
    canvasH,
    isMobile: true,
    portrait: true,
    tapToRaise,
    table: { x: 0, y: 0, w: canvasW, h: canvasH },
    seats,
    trickArea,
    scoreboard,
    biddingZone,
    upCard,
    timerRing,
    trumpBadge,
    bidBands,
    plateW,
    plateH,
    badgeW,
    badgeH,
  };
}
